// InvoicePDF.jsx
import React, { useRef } from "react";
import { Button } from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { formatHours } from "../utils/utility";

// If installed via npm, uncomment below
//import html2pdf from 'html2pdf.js';

const InvoicePDF = ({ data, title = "Invoice", isTimeChecked = false }) => {
  const printRef = useRef(null);
  const navigate = useNavigate();

  const handleDownload = async () => {
    const element = printRef.current;

    // If using CDN, html2pdf is on window
    const html2pdf = (await import("html2pdf.js")).default || window.html2pdf;

    // A4 size: 210mm x 297mm, leave margins by jsPDF unit
    const opt = {
      margin: [15, 12, 15, 12], // top, left, bottom, right in mm
      filename: `${title}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale: 2, // better text quality
        useCORS: true,
        letterRendering: true,
        ignoreElements: (el) => el.classList?.contains("no-print"),
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
      },
      pagebreak: {
        mode: ["css", "legacy"], // respects CSS page-break rules
        avoid: ["tr", ".avoid-break"], // avoid breaking rows
      },
    };

    await html2pdf().from(element).set(opt).save();
    setTimeout(() => {
      navigate("/invoice");
    }, [2000]);
  };

  return (
    <div>
      {/* PDF Content */}
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div
          className="d-flex justify-content-between"
          style={{ width: "100%", maxWidth: "210mm", padding: "8px 0" }}
        >
          <Button
            variant="success"
            className="mb-0 mt-0 d-flex gap-2 align-items-center"
            onClick={handleDownload}
          >
            <FaSave size={20} /> Save
          </Button>
          <Button
            variant="danger"
            className="mb-0 mt-0 d-flex gap-2 align-items-center"
            onClick={() => navigate("/invoice")}
          >
            <IoCloseCircle size={20} /> Close
          </Button>
        </div>
      </div>
      <div ref={printRef} className="pdf-root">
        <style>{`
          /* A4 page constraints and readable typography */
          .pdf-root {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif;
            color: #222;
            font-size: 12px;
            line-height: 1.4;
            background: #fff;
          }

          /* Container sized to A4 width for web, auto height; print uses margins */
          .page {
            width: 210mm;
            max-width: 100%;
            margin: 0 auto;
            padding: 16mm 0mm; /* visual padding in web view; print margins controlled by jsPDF margin */
            box-sizing: border-box;
            background: #fff;
          }

         /* Headings */
            .header {
            width: 100%;
            display: flex;
            justify-content: center;   /* center horizontally */
            align-items: center;       /* center vertically (relative to header’s height) */
            margin-bottom: 12px;
            border: 1px solid black;   /* remove quotes; add missing semicolon above */
            padding: 8px 0;            /* optional breathing room */
            }

            /* Keep inner content stacked and centered */
            .header-inner {
            display: flex;
            flex-direction: column;
            align-items: center;       /* center the column content */
            text-align: center;        /* center text lines */
            }

            .h1 {
            font-size: 18px;
            font-weight: 600;
            margin: 0 0 4px;
            }

            .meta {
            font-size: 11px;
            color: #555;
            }


          /* Grid for bill-from / bill-to */
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin: 12px 0 16px;
          }
          .box {
            border: 1px solid #ddd;
            padding: 8px 10px;
            border-radius: 4px;
          }
          .box h3 {
            margin: 0 0 6px;
            font-size: 12px;
            font-weight: 600;
          }

          /* Table styling */
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            word-break: break-word;
          }
          th, td {
            border: 1px solid #e5e5e5;
            padding: 6px 8px;
            text-align: left;
            vertical-align: top;
          }
          thead th {
            background: #f7f7f7;
            font-weight: 600;
          }

          /* Repeat table header on printed pages */
          thead { display: table-header-group; }
          tfoot { display: table-row-group; }
          tr { page-break-inside: avoid; }

          /* Keep totals together */
          .totals { margin-top: 12px; width: 100%; }
          .totals td { border: none; padding: 4px 0; }
          .totals .label { text-align: right; padding-right: 8px; color: #444; }
          .totals .value { width: 120px; text-align: right; font-weight: 600; }

          /* Soft section separators */
          .divider { height: 8px; }

          /* Page-break helpers for long documents */
          .page-break { page-break-before: always; }

          /* Mobile responsiveness (same template used on mobile view) */
          @media (max-width: 768px) {
            .page {
              width: 100%;
              padding: 12px;
            }
            .grid {
              grid-template-columns: 1fr;
            }
            .h1 {
              font-size: 16px;
            }
          }

          /* Print-specific tweaks if using window.print() path */
          @media print {
            .no-print { display: none !important; }
            .page {
              padding: 0; /* print margins come from jsPDF or browser print margins */
            }
          }
        `}</style>

        <div className="page">
          {/* Header */}
          <div className="header">
            <div className="header-inner">
              <div className="h1">TAX INVOICE</div>
              <div className="meta">Date: {data.invoiceDate}</div>
              <div className="meta">Inv No: {data.invoiceNumber}</div>
            </div>
          </div>

          {/* Parties */}
          <div className="grid">
            <div className="box">
              <h3>Bill From</h3>
              <div>
                <strong>Company Name:</strong> {data.billFrom.companyName}
              </div>
              {data.billFrom.abn && (
                <div>
                  <strong>ABN:</strong> {data.billFrom.abn}
                </div>
              )}
              {data.billFrom.contact && (
                <div>
                  <strong>Contact:</strong> {data.billFrom.contact}
                </div>
              )}
              {data.billFrom.address && (
                <div>
                  <strong>Address:</strong> {data.billFrom.address}
                </div>
              )}
            </div>
            <div className="box">
              <h3>Bill To</h3>
              <div>
                <strong>Company Name:</strong> {data.billTo.companyName}
              </div>
              {data.billTo.abn && (
                <div>
                  <strong>ABN:</strong> {data.billTo.abn}
                </div>
              )}
              {data.billTo.contact && (
                <div>
                  <strong>Contact:</strong> {data.billTo.contact}
                </div>
              )}
              {data.billTo.address && (
                <div>
                  <strong>Address:</strong> {data.billTo.address}
                </div>
              )}
            </div>
          </div>

          <div className="divider" />

          {/* Table */}
          <table>
            <thead>
              <tr>
                <th style={{ width: "20mm" }}>Date</th>
                <th style={{ width: "25mm" }}>Category / Route</th>
                {isTimeChecked && <>
                <th style={{ width: "20mm" }}>Start Time</th>
                <th style={{ width: "20mm" }}>End Time</th>
                </>}
                <th style={{ width: "20mm" }}>Actual working hours</th>
                <th style={{ width: "20mm" }}>Claim hours / No of Parcels</th>
                <th style={{ width: "20mm" }}>Pay Rate / Per Parcel Cost</th>
                <th style={{ width: "17mm" }}>Fuel Cost</th>
                <th style={{ width: "17mm" }}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{r.category} {' / '} {r.route}</td>
                  {isTimeChecked &&
                  <>
                  <td>{r.start}</td>
                  <td>{r.end}</td>
                  </>
                  }
                  <td>{(r.actual == 0 || parseInt(r.actual.split(':')[0]) > 15) ? 'N/A': formatHours(r.actual)}</td>
                  <td>{r.units}</td>
                  <td>{r.rate}</td>
                  <td>{r.fuel}</td>
                  <td>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <table className="totals">
            <tbody>
              <tr>
                <td className="label">Total actual working hours</td>
                <td className="value">{data.totalActualHours}</td>
                <td className="label">GST %</td>
                <td className="value">{data.gstPercent}%</td>
              </tr>
              <tr>
                <td className="label">Total hours claim</td>
                <td className="value">{data.totalHousClaim}</td>
                <td className="label">GST Included</td>
                <td className="value">{data.gstIncluded}</td>
              </tr>
              <tr>
                <td className="label">Total Parcel</td>
                <td className="value">{data.totalParcel}</td>
                <td className="label">Total</td>
                <td className="value">{data.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoicePDF;
